import { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Megaphone, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, Badge, DataTable, Pagination, useAuth } from '@aaas/shared';
import type { DataTableColumn } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { useCampaignDetailQuery } from '../../hooks/useCampaignQueries';
import { useJobListQuery } from '../../hooks/useJobQueries';
import type { Job, JobStatus } from '../../types';
import { JOB_STATUS_CONFIG } from '../../types';

// Status badge component for jobs
function JobStatusBadge({ status }: { status: JobStatus }) {
  const config = JOB_STATUS_CONFIG[status];
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    gray: 'bg-gray-100 text-gray-700',
    yellow: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
          config.color === 'green'
            ? 'bg-green-500'
            : config.color === 'yellow'
              ? 'bg-yellow-500'
              : 'bg-gray-500'
        }`}
      />
      {config.label}
    </span>
  );
}

export function CampaignDetailPage() {
  const { t } = useTranslation('ats');
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { page, size, sortBy, order, setPage, setSize, setSort } = useUrlPagination();

  // Fetch campaign details
  const {
    data: campaign,
    isLoading: campaignLoading,
    error: campaignError,
  } = useCampaignDetailQuery({
    campaignId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!campaignId,
  });

  // Fetch Jobs linked to this campaign
  const {
    data: jobResponse,
    isLoading: jobsLoading,
    error: jobsError,
  } = useJobListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    campaignIds: campaignId ? [campaignId] : undefined,
    enabled: !!user && !!currentWorkspace && !!campaignId,
    refetchInterval: 5000,
  });

  // Extract data from response
  const jobs = jobResponse?.data?.items || [];
  const totalItems = jobResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleBack = () => {
    navigate('/ats/campaigns');
  };

  const handleJobClick = useCallback(
    (record: Job) => {
      navigate(`/ats/jobs/${record.id}`);
    },
    [navigate]
  );

  // Define table columns for Jobs
  const columns: DataTableColumn<Job>[] = [
    {
      key: 'code',
      title: t('campaign.detail.columns.code'),
      sortable: true,
      render: (value: string, record: Job) => (
        <button
          onClick={() => handleJobClick(record)}
          className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-left font-mono"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'position',
      title: t('campaign.detail.columns.position'),
      render: (_value, record: Job) => (
        <span className="text-gray-900">{record.position?.name || '-'}</span>
      ),
    },
    {
      key: 'status',
      title: t('campaign.detail.columns.status'),
      width: 100,
      render: (value: JobStatus) => <JobStatusBadge status={value} />,
    },
    {
      key: 'quantity',
      title: t('campaign.detail.columns.qty'),
      width: 60,
      render: (value: number) => <span className="text-gray-600">{value}</span>,
    },
    {
      key: 'start_date',
      title: t('campaign.detail.columns.startDate'),
      width: 120,
      sortable: true,
      render: (value: string | null) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    {
      key: 'created_at',
      title: t('campaign.detail.columns.created'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const loading = campaignLoading || jobsLoading;
  const error = campaignError || jobsError;

  if (loading && !campaign) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  if (campaignError) {
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('campaign.detail.back')}
        </button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {campaignError instanceof Error ? campaignError.message : t('campaign.detail.loadFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('campaign.detail.back')}
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Megaphone className="h-8 w-8 text-green-600" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
                <Badge
                  className={
                    campaign.status === 'active'
                      ? 'bg-green-100 text-green-800 hover:bg-green-100'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-100'
                  }
                >
                  {campaign.status === 'active' ? t('campaign.statusActive') : t('campaign.statusInactive')}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 font-mono">{campaign.code}</p>
            </div>
          </div>
        </div>

        {/* Campaign metadata */}
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
          {campaign.description && (
            <div>
              <span className="text-gray-400">{t('campaign.detail.description')}</span>{' '}
              <span className="font-medium">{campaign.description}</span>
            </div>
          )}
          <div>
            <span className="text-gray-400">{t('campaign.detail.totalJobs')}</span>{' '}
            <span className="font-medium">{totalItems}</span>
          </div>
          <div>
            <span className="text-gray-400">{t('campaign.detail.created')}</span>{' '}
            <span className="font-medium">
              {new Date(campaign.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('campaign.detail.loadJobsFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={jobs}
          loading={jobsLoading}
          rowKey={(record) => record.id}
          emptyMessage={t('campaign.detail.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>
    </div>
  );
}
